import type {
  CreatePhotoRequest,
  Photo,
  UpdatePhotoRequest,
} from "../types/api";
import { getAuthHeaders } from "../utils/auth";

/**
 * Create a new photo metadata entry
 */
export const createPhoto = async (data: CreatePhotoRequest): Promise<Photo> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/photos`, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const photoData: Photo = await response.json();
    return photoData;
  } catch (error) {
    console.error("Error creating photo:", error);
    throw error;
  }
};

/**
 * Get a single photo by ID
 */
export const getPhoto = async (photoId: string): Promise<Photo> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/photos/${photoId}`, { headers });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const photoData: Photo = await response.json();
    return photoData;
  } catch (error) {
    console.error("Error fetching photo:", error);
    throw error;
  }
};

/**
 * Get all photos for a specific child
 */
export const getPhotosByChild = async (
  campusId: string,
  childId: string,
): Promise<Photo[]> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `/api/photos?campusId=${campusId}&childId=${childId}`,
      { headers },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const photosData: Photo[] = await response.json();
    return photosData;
  } catch (error) {
    console.error("Error fetching photos by child:", error);
    throw error;
  }
};

/**
 * Get all photos for a specific event
 */
export const getPhotosByEvent = async (eventId: string): Promise<Photo[]> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/photos?eventId=${eventId}`, { headers });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const photosData: Photo[] = await response.json();
    return photosData;
  } catch (error) {
    console.error("Error fetching photos by event:", error);
    throw error;
  }
};

/**
 * Update a photo metadata (e.g., add finalUrl after purchase)
 */
export const updatePhoto = async (
  photoId: string,
  data: UpdatePhotoRequest,
): Promise<Photo> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/photos/${photoId}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const photoData: Photo = await response.json();
    return photoData;
  } catch (error) {
    console.error("Error updating photo:", error);
    throw error;
  }
};

/**
 * Delete a photo and its metadata
 */
export const deletePhoto = async (photoId: string): Promise<void> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/photos/${photoId}`, {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (error) {
    console.error("Error deleting photo:", error);
    throw error;
  }
};
